import React, { useCallback, useEffect, useRef } from 'react';
import { cn } from '../ui';

export interface BhajanVideoModalProps {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Accessible label / title for the video (e.g. the bhajan name). */
  title?: string;
}

/**
 * In-site YouTube player modal. Renders a 16:9 embed via the Google-owned
 * privacy-enhanced domain (youtube-nocookie.com) with NO autoplay — the user
 * taps YouTube's native play button, which is what makes the view count toward
 * the channel. The iframe only mounts while the modal is open.
 *
 * Accessibility: role=dialog/aria-modal, ESC + backdrop + close button to
 * dismiss, focus moves into the modal on open and is restored on close, Tab is
 * trapped, and body scroll is locked.
 */
export const BhajanVideoModal: React.FC<BhajanVideoModalProps> = ({ videoId, isOpen, onClose, title }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const label = title ? `${title} — Bhajan Babies video` : 'Bhajan Babies video';
  // No autoplay (so the user-initiated play counts). rel=0 keeps related videos
  // to the same channel; modestbranding trims YouTube chrome.
  const src = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
  const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;

  // ESC to close + body scroll lock + focus management.
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog.
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, onClose]);

  // Trap Tab within the modal's focusable elements.
  const onKeyDownTrap = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'button, a[href], iframe, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
      onKeyDown={onKeyDownTrap}
    >
      <div className="absolute inset-0 bg-bg-0/85 backdrop-blur-sm" aria-hidden />

      <div
        ref={panelRef}
        className="relative w-full max-w-4xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Controls row above the video so they never overlap the player chrome */}
        <div className="flex items-center justify-between mb-2 px-1">
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-small text-text-secondary hover:text-text-primary transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path fill="#FF0000" d="M23 7.6a3 3 0 0 0-2.1-2.1C19.1 5 12 5 12 5s-7.1 0-8.9.5A3 3 0 0 0 1 7.6 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.4a3 3 0 0 0 2.1 2.1c1.8.5 8.9.5 8.9.5s7.1 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.6z" />
              <path fill="white" d="M9.75 15.5V8.5l6 3.5z" />
            </svg>
            Watch on YouTube ↗
          </a>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close video"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-bg-2/80 border border-light text-text-primary hover:bg-bg-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>
        </div>

        {/* 16:9 player — CSS aspect-ratio avoids layout shift at every width */}
        <div className={cn('w-full overflow-hidden rounded-2xl border border-light bg-black shadow-4')} style={{ aspectRatio: '16 / 9' }}>
          <iframe
            src={src}
            title={label}
            className="w-full h-full"
            style={{ border: 0 }}
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};
